import { useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppContext } from '~/contexts';
import { DeviceDashboardLiveDocument } from '../queries/dashboard-live.generated';
import { DeviceDashboardDocument } from '../queries/dashboard.generated';
import { Additional, Commands, Details, Layout, MiniPreview, Screenshot } from './components';
import { SlowYourGrid } from './lib';

export const DeviceDashboard = () => {
  const params = useParams<{ deviceId: string }>();
  const deviceId = Number(params.deviceId);

  // Static data - fetched once
  const { currentUser } = useAppContext();
  const { data: staticData } = useQuery(DeviceDashboardDocument, {
    variables: { deviceId },
  });

  const isDemo = staticData?.device?.demo;

  // Live data - polled every 10 seconds unless the user turned auto-refresh off (demo devices
  // skip polling for stable UI).
  // Hold pollInterval at 0 until staticData resolves so the live query never starts
  // polling for a demo device, even briefly, before the gate evaluates.
  const { data: liveData } = useQuery(DeviceDashboardLiveDocument, {
    variables: { deviceId },
    pollInterval: isDemo === false && currentUser.autoRefresh ? 10_000 : 0,
  });

  // Merge static and live data
  const device = useMemo(() => {
    if (!staticData?.device || !liveData?.device) return undefined;
    return {
      ...staticData.device,
      ...liveData.device,
    };
  }, [staticData?.device, liveData?.device]);

  return (
    <Layout>
      {device && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={9} xl={8}>
            {/* A wrapped column line is sized to its widest item, so long values
                (e.g. switch names) would stretch these panels past the column. */}
            <Grid container direction="column" flexWrap="nowrap" spacing={2}>
              <SlowYourGrid item>
                <Details device={device} />
              </SlowYourGrid>
              <SlowYourGrid item>
                <Commands device={device} />
              </SlowYourGrid>
              <Grid item>
                <Additional device={device} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={3} xl={4}>
            <Grid container direction="column" flexWrap="nowrap" spacing={2}>
              <Grid item>
                <Screenshot device={device} />
              </Grid>
              <Grid item>
                <MiniPreview key={device.id} device={device} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Layout>
  );
};
